/**
 * AI Assistant panel with chat and quick actions
 */

import React, { useState, useRef, useEffect } from "react";
import {
  Input,
  Button,
  Space,
  Typography,
  Card,
  Spin,
  message,
  List,
} from "antd";
import {
  SendOutlined,
  PhoneOutlined,
  MailOutlined,
  FileTextOutlined,
  SearchOutlined,
  BankOutlined,
  DollarOutlined,
  UserOutlined,
  RightOutlined,
  CloseCircleOutlined,
  CheckCircleFilled,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useChatStore } from "@/hooks/useChatStore";
import { useContextStore } from "@/hooks/useContextStore";
import { ChatMessage, ResultSection, CRMContext } from "@/types";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

// State for pending smart create operation
interface PendingCreate {
  module: string;
  data: Record<string, unknown>;
  missingFields: Array<{
    name: string;
    label: string;
    type: string;
  }>;
  currentFieldIndex: number;
}

export const AssistantPanel: React.FC = () => {
  const [input, setInput] = useState("");
  const [pendingCreate, setPendingCreate] = useState<PendingCreate | null>(
    null
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { messages, isLoading, addMessage, setLoading, setError } =
    useChatStore();
  const {
    currentAccountId,
    cachedAccounts,
    setCurrentAccount,
    setCurrentContact,
    setCurrentOpportunity,
    activeContextId,
    contexts,
    clearActiveContext,
    getContextPayload,
    addEntityToContext,
  } = useContextStore();

  // Get active context for display
  const activeContext = activeContextId ? contexts.get(activeContextId) : null;

  // Handle adding entity to active context
  const handleAddToContext = (entity: Record<string, unknown>) => {
    if (!activeContextId) return;

    let entityType: "accounts" | "contacts" | "opportunities" | "tickets";

    if (entity.accountname) {
      entityType = "accounts";
    } else if (entity.potentialname) {
      entityType = "opportunities";
    } else if (entity.firstname || entity.lastname) {
      entityType = "contacts";
    } else if (entity.ticket_title) {
      entityType = "tickets";
    } else {
      return;
    }

    addEntityToContext(activeContextId, entityType, entity.id as string);
    message.success(`Added to ${activeContext?.label || "context"}`);
  };

  // Handle entity click - navigate and set context
  const handleEntityClick = (entity: Record<string, unknown>) => {
    if (entity.accountname) {
      setCurrentAccount(entity.id as string);
      navigate(`/accounts/${entity.id}`);
    } else if (entity.potentialname) {
      setCurrentOpportunity(entity.id as string);
      navigate(`/opportunities/${entity.id}`);
    } else if (entity.firstname || entity.lastname) {
      setCurrentContact(entity.id as string);
      navigate(`/contacts/${entity.id}`);
    }
  };

  const currentAccount = currentAccountId
    ? cachedAccounts.get(currentAccountId)
    : null;

  // Scroll to bottom when messages change (only if there are messages)
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Quick actions based on context
  const quickActions = [
    {
      icon: <PhoneOutlined />,
      label: "Log call",
      action: () => handleQuickAction("log_call"),
    },
    {
      icon: <MailOutlined />,
      label: "Draft email",
      action: () => handleQuickAction("draft_email"),
    },
    {
      icon: <FileTextOutlined />,
      label: "Prep brief",
      action: () => handleQuickAction("prep_brief"),
    },
    {
      icon: <SearchOutlined />,
      label: "Find...",
      action: () => handleQuickAction("search"),
    },
  ];

  const handleQuickAction = async (actionType: string) => {
    switch (actionType) {
      case "log_call":
        setInput("Log a call with ");
        break;
      case "draft_email":
        setInput("Draft a follow-up email for ");
        break;
      case "prep_brief":
        if (currentAccount) {
          await handleSend(`Prepare a brief for ${currentAccount.accountname}`);
        } else {
          setInput("Prepare a brief for ");
        }
        break;
      case "search":
        setInput("/find ");
        break;
    }
  };

  // Handle user input during pending create flow
  const handlePendingCreateInput = async (userInput: string) => {
    if (!pendingCreate) return;

    const { module, data, missingFields, currentFieldIndex } = pendingCreate;
    const currentField = missingFields[currentFieldIndex];

    // Add the provided value to the data
    const updatedData = {
      ...data,
      [currentField.name]: userInput.trim(),
    };

    // Check if there are more fields to collect
    const nextIndex = currentFieldIndex + 1;

    if (nextIndex < missingFields.length) {
      // Ask for the next field
      const nextField = missingFields[nextIndex];

      setPendingCreate({
        ...pendingCreate,
        data: updatedData,
        currentFieldIndex: nextIndex,
      });

      addMessage({
        role: "assistant",
        content: `Got it! **${currentField.label}**: ${userInput}\n\nNow please provide the **${nextField.label}**:`,
      });

      setLoading(false);
    } else {
      // All fields collected, perform the create
      setPendingCreate(null);

      try {
        const toolStart = performance.now();
        const createResponse = await axios.post("/api/smart/create", {
          module,
          data: updatedData,
        });
        const toolTime = performance.now() - toolStart;

        let resultText = "";
        if (createResponse.data.success) {
          const result = createResponse.data.result;
          const entityType =
            module === "Accounts"
              ? "Account"
              : module === "Contacts"
              ? "Contact"
              : "Deal";
          const name =
            result.accountname ||
            result.potentialname ||
            `${result.firstname || ""} ${result.lastname || ""}`.trim() ||
            "Unknown";
          resultText = `✅ Created ${entityType}: **${name}**\n\nID: ${result.id}`;
        } else if (createResponse.data.needs_input) {
          // Still missing fields (shouldn't happen, but handle it)
          const stillMissing = createResponse.data.missing_fields;
          resultText = `❌ Still missing required fields: ${stillMissing
            .map((f: { label: string }) => f.label)
            .join(", ")}`;
        } else {
          resultText = `❌ Create failed: ${
            createResponse.data.error ||
            createResponse.data.message ||
            "Unknown error"
          }`;
        }

        const timingFooter = `\n\n────────────────────\n⏱ Create: ${(
          toolTime / 1000
        ).toFixed(1)}s`;

        addMessage({
          role: "assistant",
          content: resultText + timingFooter,
          metadata: {
            toolName: "crm_create",
            timing: {
              dspy: 0,
              tool: toolTime,
              total: toolTime,
            },
          },
        });
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : "Unknown error";
        addMessage({
          role: "assistant",
          content: `❌ Create failed: ${errorMsg}`,
        });
      }

      setLoading(false);
    }
  };

  const handleSend = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text) return;

    // Add user message
    addMessage({ role: "user", content: text });
    setInput("");
    setLoading(true);

    try {
      // Check if we're in a pending create flow (user providing missing field value)
      if (pendingCreate) {
        await handlePendingCreateInput(text);
        return;
      }

      // Use DSPy router for intelligent intent classification
      let toolName = "";
      let routingInfo = "";

      // Build conversation history for context
      const conversationHistory = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      // Get last results from previous assistant message (if any)
      let lastResults: Record<string, unknown>[] | null = null;
      const lastAssistantMsg = [...messages]
        .reverse()
        .find((m) => m.role === "assistant");
      if (lastAssistantMsg?.metadata?.results) {
        lastResults = lastAssistantMsg.metadata.results as Record<
          string,
          unknown
        >[];
      }

      // Get context payload if active
      const contextPayload = getContextPayload();

      // Determine account ID: panel selection takes priority, then first account from active context
      let effectiveAccountId = currentAccountId;
      if (!effectiveAccountId && contextPayload?.entities?.accounts?.length) {
        effectiveAccountId = contextPayload.entities.accounts[0];
      }

      // Get current user ID from localStorage
      let currentUserId: string | null = null;
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          currentUserId = user.id;
        }
      } catch {
        // Ignore parse errors
      }

      // Call multi-tool DSPy router (routes and executes in one call)
      const multiStart = performance.now();
      const multiResponse = await axios.post("/api/mcp/route-multi", {
        message: text,
        current_account_id: effectiveAccountId,
        current_contact_id: null,
        current_opportunity_id: null,
        current_user_id: currentUserId,
        conversation_history: conversationHistory,
        last_results: lastResults,
        context: contextPayload,
      });

      const totalTime = performance.now() - multiStart;

      const { results, confidence, reasoning } = multiResponse.data;
      routingInfo = `[${Math.round(confidence * 100)}%] ${reasoning}`;

      console.log(
        "Multi-tool routing:",
        results,
        routingInfo,
        `(${(totalTime / 1000).toFixed(2)}s)`
      );

      // Check if any result is a create operation that needs validation
      const createResult = results.find(
        (r: {
          tool_name: string;
          success: boolean;
          result?: { needs_input?: boolean };
        }) => r.tool_name === "crm_create" && r.success && r.result?.needs_input
      );

      if (createResult?.result?.needs_input) {
        const { missing_fields, provided_data } = createResult.result;
        const module = createResult.module || "Unknown";

        setPendingCreate({
          module,
          data: provided_data || {},
          missingFields: missing_fields,
          currentFieldIndex: 0,
        });

        const firstField = missing_fields[0];
        const fieldList = missing_fields
          .map((f: { label: string }) => `• ${f.label}`)
          .join("\n");

        const timingFooter = `\n\n────────────────────\n⏱ Total: ${(
          totalTime / 1000
        ).toFixed(1)}s`;

        addMessage({
          role: "assistant",
          content: `To create a new **${module}** record, I need some additional information.\n\n**Required fields:**\n${fieldList}\n\nPlease provide the **${firstField.label}**:${timingFooter}`,
          metadata: {
            toolName: "smart_create_validation",
            timing: {
              dspy: totalTime,
              tool: 0,
              total: totalTime,
            },
          },
        });

        setLoading(false);
        return;
      }

      // Process all results and format them
      let resultText = "";
      let allParsedData: Record<string, unknown>[] = [];
      let resultSections: Array<{
        toolName: string;
        module: string;
        data: Record<string, unknown>[];
      }> = [];

      for (const toolResult of results) {
        const { tool_name, module, success, result, error } = toolResult;
        toolName = tool_name; // For metadata

        if (!success) {
          resultText += `❌ **${tool_name}** failed: ${error}\n\n`;
          continue;
        }

        // Parse the result data
        let data = result;
        if (typeof result === "string") {
          try {
            data = JSON.parse(result);
          } catch {
            data = result;
          }
        }

        // Format based on tool type
        if (tool_name === "crm_create") {
          if (data?.id) {
            const entityType =
              module === "Accounts"
                ? "Account"
                : module === "Contacts"
                ? "Contact"
                : "Deal";
            const name =
              data.accountname ||
              data.potentialname ||
              `${data.firstname || ""} ${data.lastname || ""}`.trim() ||
              "Unknown";
            resultText += `✅ Created ${entityType}: **${name}**\nID: ${data.id}\n\n`;
          }
        } else if (tool_name === "get_pipeline_summary") {
          resultText += `**Pipeline Summary**\n\nTotal Deals: ${
            data.total_deals
          }\nTotal Value: $${data.total_value?.toLocaleString()}\n\n`;
        } else if (tool_name === "find_at_risk_deals") {
          resultText += `**At-Risk Deals**\n\nStale: ${
            data.stale_deals?.count || 0
          } | Overdue: ${
            data.overdue_deals?.count || 0
          } | Value: $${data.total_at_risk_value?.toLocaleString()}\n\n`;
        } else if (tool_name === "ai_customer_brief") {
          // Format the AI-generated customer brief
          const account = data.account || {};
          const brief = data.brief || {};

          resultText += `## Customer Brief: ${account.name || "Unknown"}\n\n`;

          if (brief.executive_summary) {
            resultText += `### Executive Summary\n${brief.executive_summary}\n\n`;
          }
          if (brief.key_insights) {
            resultText += `### Key Insights\n${brief.key_insights}\n\n`;
          }
          if (brief.opportunities) {
            resultText += `### Opportunities\n${brief.opportunities}\n\n`;
          }
          if (brief.risks) {
            resultText += `### Risks\n${brief.risks}\n\n`;
          }
          if (brief.recommended_actions) {
            resultText += `### Recommended Actions\n${brief.recommended_actions}\n\n`;
          }
        } else if (tool_name === "prepare_customer_brief") {
          // Format raw customer brief data
          const account = data.account || {};
          const summary = data.summary || {};

          resultText += `## Customer Brief: ${account.name || "Unknown"}\n\n`;
          resultText += `**Industry:** ${
            account.industry || "N/A"
          } | **Rating:** ${account.rating || "N/A"}\n`;
          resultText += `**Revenue:** $${Number(
            account.annual_revenue || 0
          ).toLocaleString()} | **Employees:** ${
            account.employees || "N/A"
          }\n\n`;

          resultText += `### Summary\n`;
          resultText += `- Contacts: ${summary.total_contacts || 0}\n`;
          resultText += `- Open Opportunities: ${
            summary.total_opportunities || 0
          }\n`;
          resultText += `- Pipeline Value: $${Number(
            summary.total_pipeline_value || 0
          ).toLocaleString()}\n`;
          resultText += `- Open Tickets: ${summary.open_tickets || 0}\n\n`;
        } else if (tool_name === "ai_draft_email") {
          // Format drafted email
          resultText += `## Draft Email\n\n`;
          resultText += `**Subject:** ${data.subject || "No subject"}\n\n`;
          resultText += `---\n\n${data.body || "No content"}\n\n`;
        } else if (tool_name === "ai_mail_merge") {
          // Format mail merge results
          const merged = data.merged || [];
          resultText += `## Mail Merge Results\n\n`;
          resultText += `Generated ${
            merged.length
          } personalized message(s) for ${data.module || "records"}\n\n`;

          merged.forEach(
            (
              item: { record_id: string; record_name: string; content: string },
              idx: number
            ) => {
              resultText += `### ${idx + 1}. ${
                item.record_name || item.record_id
              }\n`;
              resultText += `${item.content}\n\n`;
            }
          );
        } else if (Array.isArray(data)) {
          // Store for clickable results
          if (data.length > 0) {
            resultSections.push({
              toolName: tool_name,
              module: module || "Unknown",
              data: data,
            });
            allParsedData = [...allParsedData, ...data];
          } else {
            resultText += `**${module || "Search"}**: No results found.\n\n`;
          }
        } else if (data && typeof data === "object" && data.id) {
          // Single entity
          resultSections.push({
            toolName: tool_name,
            module: module || "Unknown",
            data: [data],
          });
          allParsedData.push(data);
        }
      }

      // Add timing footer
      const timingFooter = `\n\n────────────────────\n⏱ Total: ${(
        totalTime / 1000
      ).toFixed(1)}s (${results.length} tool${results.length > 1 ? "s" : ""})`;

      // If we have clickable results sections, format them specially
      if (resultSections.length > 0) {
        if (resultSections.length === 1) {
          // Single result set - show as before
          resultText = `Found ${allParsedData.length} result(s):`;
        } else {
          // Multiple result sets - add headers
          resultText = `**Results from ${results.length} queries:**\n\n`;
          resultSections.forEach((section) => {
            resultText += `📋 **${section.module}** (${section.data.length})\n`;
          });
        }
      }

      addMessage({
        role: "assistant",
        content: resultText + timingFooter,
        metadata: {
          results: allParsedData.length > 0 ? allParsedData : undefined,
          resultSections:
            resultSections.length > 1 ? resultSections : undefined,
          toolName,
          confidence,
          timing: {
            dspy: 0,
            tool: totalTime,
            total: totalTime,
          },
        },
      });
    } catch (error) {
      let errorMsg = "Failed to process request";

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 503) {
          errorMsg =
            "DSPy router not available. Please check OpenRouter API key configuration.";
        } else if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail;
        } else {
          errorMsg = error.message;
        }
      } else if (error instanceof Error) {
        errorMsg = error.message;
      }

      setError(errorMsg);
      message.error(errorMsg);
      addMessage({
        role: "assistant",
        content: `⚠️ **Routing Error**\n\n${errorMsg}\n\nPlease ensure the backend API is running and OpenRouter API key is configured.`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        padding: 20,
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <Text
          strong
          style={{ fontSize: 15, color: "#1a1a2e", letterSpacing: "-0.2px" }}
        >
          Assistant
        </Text>
        {currentAccount && (
          <Text type="secondary" style={{ marginLeft: 8, fontSize: 13 }}>
            · {currentAccount.accountname}
          </Text>
        )}
      </div>

      {/* Quick Actions */}
      <Space wrap style={{ marginBottom: 16 }}>
        {quickActions.map((action, idx) => (
          <Button
            key={idx}
            size="small"
            icon={action.icon}
            onClick={action.action}
            style={{
              borderRadius: 6,
              border: "1px solid #e2e8f0",
              color: "#64748b",
              fontWeight: 500,
              transition: "all 0.15s ease",
            }}
          >
            {action.label}
          </Button>
        ))}
      </Space>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          marginBottom: 16,
          paddingRight: 8,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {messages.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "20px 16px",
              color: "#999",
            }}
          >
            <Text type="secondary" style={{ fontSize: 13 }}>
              Ask questions about your CRM data, create records, or use quick
              actions above.
            </Text>
          </div>
        ) : (
          messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              onEntityClick={handleEntityClick}
              onAddToContext={handleAddToContext}
              activeContextId={activeContextId}
              activeContext={activeContext}
            />
          ))
        )}
        {isLoading && (
          <div style={{ textAlign: "center", padding: 16 }}>
            <Spin size="small" />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div>
        {/* Context indicator */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            marginBottom: 10,
            background: activeContext ? "#f0fdf4" : "#f8f9fa",
            borderRadius: 8,
            border: `1px solid ${activeContext ? "#bbf7d0" : "#e2e8f0"}`,
            transition: "all 0.15s ease",
          }}
        >
          {activeContext ? (
            <>
              <Space size="small">
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: activeContext.color,
                    display: "inline-block",
                  }}
                />
                <Text style={{ fontSize: 12 }}>
                  <CheckCircleFilled
                    style={{ color: "#52c41a", marginRight: 4 }}
                  />
                  {activeContext.label}
                </Text>
              </Space>
              <Button
                type="text"
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={clearActiveContext}
                style={{ fontSize: 11 }}
              >
                Clear
              </Button>
            </>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              No context active - click a context in the panel to activate
            </Text>
          )}
        </div>

        <TextArea
          data-assistant-input="true"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            activeContext
              ? `Ask about ${activeContext.label}...`
              : "Type naturally or use /commands..."
          }
          autoSize={{ minRows: 1, maxRows: 4 }}
          disabled={isLoading}
        />
        <div style={{ marginTop: 8, textAlign: "right" }}>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => handleSend()}
            disabled={!input.trim() || isLoading}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

// Message bubble component
const MessageBubble: React.FC<{
  message: ChatMessage;
  onEntityClick?: (entity: Record<string, unknown>) => void;
  onAddToContext?: (entity: Record<string, unknown>) => void;
  activeContextId?: string | null;
  activeContext?: CRMContext | null;
}> = ({
  message,
  onEntityClick,
  onAddToContext,
  activeContextId,
  activeContext,
}) => {
  const isUser = message.role === "user";
  const hasClickableResults =
    !isUser && message.metadata?.results && message.metadata.results.length > 0;

  // Check if an entity is in the active context
  const isInContext = (item: Record<string, unknown>): boolean => {
    if (!activeContext?.entities) return false;
    const id = item.id as string;
    if (!id) return false;

    if (item.accountname) {
      return activeContext.entities.accounts.includes(id);
    } else if (item.potentialname) {
      return activeContext.entities.opportunities.includes(id);
    } else if (item.firstname || item.lastname) {
      return activeContext.entities.contacts.includes(id);
    } else if (item.ticket_title) {
      return activeContext.entities.tickets.includes(id);
    }
    return false;
  };

  // Render a single list of entities
  const renderEntityList = (
    items: Record<string, unknown>[],
    sectionLabel?: string
  ) => {
    return (
      <div style={{ marginTop: sectionLabel ? 4 : 8 }}>
        {sectionLabel && (
          <Text
            strong
            style={{
              fontSize: 12,
              color: "#1890ff",
              display: "block",
              marginBottom: 4,
            }}
          >
            {sectionLabel}
          </Text>
        )}
        <List
          size="small"
          dataSource={items}
          renderItem={(item: Record<string, unknown>) => {
            let icon = <BankOutlined />;
            let title = "";
            let description = "";

            if (item.accountname) {
              icon = <BankOutlined style={{ color: "#1890ff" }} />;
              title = item.accountname as string;
              description = (item.industry as string) || "N/A";
            } else if (item.potentialname) {
              icon = <DollarOutlined style={{ color: "#52c41a" }} />;
              title = item.potentialname as string;
              description = `${item.sales_stage || "N/A"} · $${Number(
                item.amount || 0
              ).toLocaleString()}`;
            } else if (item.firstname || item.lastname) {
              icon = <UserOutlined style={{ color: "#faad14" }} />;
              title = `${item.firstname || ""} ${item.lastname || ""}`.trim();
              description = (item.email as string) || "";
            }

            const inContext = isInContext(item);

            return (
              <List.Item
                style={{
                  padding: "6px 8px",
                  cursor: "pointer",
                  borderRadius: 4,
                  marginBottom: 4,
                  background: inContext ? "#f6ffed" : "#fff",
                  border: inContext
                    ? `1px solid ${activeContext?.color || "#52c41a"}`
                    : "1px solid transparent",
                }}
                onClick={() => onEntityClick?.(item)}
                actions={
                  activeContextId && onAddToContext && !inContext
                    ? [
                        <Button
                          key="add"
                          type="text"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddToContext(item);
                          }}
                          style={{ fontSize: 11 }}
                        >
                          Add
                        </Button>,
                      ]
                    : inContext
                    ? [
                        <Text key="in" type="success" style={{ fontSize: 11 }}>
                          In context
                        </Text>,
                      ]
                    : undefined
                }
              >
                <List.Item.Meta
                  avatar={icon}
                  title={
                    <Space>
                      <Text strong style={{ fontSize: 13 }}>
                        {title}
                      </Text>
                      <RightOutlined style={{ fontSize: 10, color: "#999" }} />
                    </Space>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {description}
                    </Text>
                  }
                />
              </List.Item>
            );
          }}
        />
      </div>
    );
  };

  // Render clickable entity list (handles both single and multi-section results)
  const renderClickableResults = () => {
    if (!message.metadata?.results) return null;

    const results = message.metadata.results;
    const sections = message.metadata.resultSections;

    return (
      <div style={{ marginTop: 8 }}>
        {/* Add All button when context is active */}
        {activeContextId && onAddToContext && results.length > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 8,
            }}
          >
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => {
                results.forEach((item: Record<string, unknown>) => {
                  onAddToContext(item);
                });
              }}
              style={{ fontSize: 11 }}
            >
              Add All ({results.length})
            </Button>
          </div>
        )}

        {/* Render sections if we have multiple, otherwise render flat list */}
        {sections && sections.length > 1
          ? sections.map((section: ResultSection, idx: number) => (
              <div
                key={idx}
                style={{ marginBottom: idx < sections.length - 1 ? 12 : 0 }}
              >
                {renderEntityList(
                  section.data,
                  `${section.module} (${section.data.length})`
                )}
              </div>
            ))
          : renderEntityList(results)}
      </div>
    );
  };

  // Extract timing from content or metadata
  const renderTiming = () => {
    if (isUser) return null;

    const timing = message.metadata?.timing;
    if (timing) {
      // Format timing display based on what's available
      let timingText = "";
      if (timing.dspy > 0 && timing.tool > 0) {
        // Both DSPy and Tool timing available (old single-tool mode)
        timingText = `DSPy: ${(timing.dspy / 1000).toFixed(1)}s | Tool: ${(
          timing.tool / 1000
        ).toFixed(1)}s | Total: ${(timing.total / 1000).toFixed(1)}s`;
      } else {
        // Combined timing (multi-tool mode)
        timingText = `Total: ${(timing.total / 1000).toFixed(1)}s`;
      }

      return (
        <Text
          type="secondary"
          style={{ fontSize: 11, display: "block", marginTop: 8 }}
        >
          ────────────────────
          <br />⏱ {timingText}
        </Text>
      );
    }
    return null;
  };

  return (
    <div
      style={{
        marginBottom: 12,
        textAlign: isUser ? "right" : "left",
      }}
    >
      <Card
        size="small"
        style={{
          display: isUser ? "inline-block" : "block",
          maxWidth: isUser ? "85%" : "100%",
          textAlign: "left",
          background: isUser ? "#3d4f5f" : "#f8f9fa",
          border: "none",
          borderRadius: 12,
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
        }}
        styles={{ body: { padding: "10px 14px" } }}
      >
        {hasClickableResults ? (
          <>
            <Text style={{ fontSize: 13 }}>
              Found {message.metadata!.results!.length} result(s):
            </Text>
            {renderClickableResults()}
            {renderTiming()}
          </>
        ) : (
          <Paragraph
            style={{
              margin: 0,
              color: isUser ? "#fff" : "inherit",
              whiteSpace: "pre-wrap",
            }}
          >
            {message.content}
          </Paragraph>
        )}
      </Card>
    </div>
  );
};
